/**
 * The one pacer every request of a community pull or push goes through, reads and writes alike
 * (gh #5003, Pull and push).
 *
 * Every `/api/communities/*` route shares one per-person budget (`'/api/communities/*'` in
 * `packages/core/src/rate-limits.ts`), which is also the person's send limit and what their web
 * console uses. The pacer starts at most `COMMUNITY_REQUESTS_PER_MINUTE` requests in any minute,
 * leaving the rest of that budget to the console and to sends. A 429 — the author's other use
 * spent it — waits the `details.retryAfter` its body carries (`ApiError` exposes no headers) and
 * sends the request again: a rate-limited request reached no route, so a write is retried safely.
 *
 * It lives here, not in `ApiClient`, because each budget is paced by the commands that share it:
 * `wayai files pull`/`push` pace the data surface's own budget with this class too, at their own
 * rate (`data/files-sync/sync.ts`).
 */

import { ApiError } from '../lib/api-client.js';
import { retryAfterSeconds } from './api.js';

/** The requests a run starts in any minute at most. */
export const COMMUNITY_REQUESTS_PER_MINUTE = 50;

const WINDOW_MS = 60_000;
/** The wait for a 429 whose body names none: the budget's own window. */
const DEFAULT_RETRY_AFTER_SECONDS = 60;
/** How many 429s one request waits out before its error stands. */
export const MAX_RATE_LIMITED_RETRIES = 5;

export interface PacerOptions {
  perMinute?: number;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  /** Told before every wait, so a long run says why it is idle. */
  onWait?: (ms: number, reason: 'paced' | 'rate_limited') => void;
}

const realSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export class Pacer {
  private readonly perMinute: number;
  private readonly now: () => number;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly onWait?: PacerOptions['onWait'];
  /** When each request of the last minute started, oldest first. */
  private starts: number[] = [];

  constructor(options: PacerOptions = {}) {
    this.perMinute = options.perMinute ?? COMMUNITY_REQUESTS_PER_MINUTE;
    this.now = options.now ?? Date.now;
    this.sleep = options.sleep ?? realSleep;
    this.onWait = options.onWait;
  }

  /** Send one request when the pace allows it, waiting out up to `MAX_RATE_LIMITED_RETRIES` 429s. */
  async run<T>(send: () => Promise<T>): Promise<T> {
    for (let attempt = 0; ; attempt++) {
      await this.takeSlot();
      try {
        return await send();
      } catch (err) {
        if (!(err instanceof ApiError) || err.status !== 429 || attempt >= MAX_RATE_LIMITED_RETRIES) throw err;
        const ms = (retryAfterSeconds(err) ?? DEFAULT_RETRY_AFTER_SECONDS) * 1000;
        this.onWait?.(ms, 'rate_limited');
        await this.sleep(ms);
      }
    }
  }

  private async takeSlot(): Promise<void> {
    for (;;) {
      const now = this.now();
      this.starts = this.starts.filter((start) => now - start < WINDOW_MS);
      if (this.starts.length < this.perMinute) {
        this.starts.push(now);
        return;
      }
      const ms = this.starts[0] + WINDOW_MS - now;
      this.onWait?.(ms, 'paced');
      await this.sleep(ms);
    }
  }
}

/** "under a minute" / "about 3 minutes": how long `requests` take at the pace. */
export function paceDuration(requests: number, perMinute = COMMUNITY_REQUESTS_PER_MINUTE): string {
  if (requests <= perMinute) return 'under a minute';
  const minutes = Math.ceil(requests / perMinute) - 1;
  return minutes === 1 ? 'about a minute' : `about ${minutes} minutes`;
}
